import type { ReactNode } from "react";
import { useEffect, useState, createContext } from "react";

import { useServer } from "@shared/hooks";
import { breakpoints } from "@styles/emotion-styles/dimensions";

type Breakpoint = keyof typeof breakpoints;
interface BreakpointDetails {
  min: number;
  max: number;
  active: boolean;
  greaterThan: boolean;
  lessThan: boolean;
}

interface BreakpointsActiveMap extends Record<Breakpoint, BreakpointDetails> {
  width: number;
}

interface Props {
  children: ReactNode;
}

const BreakpointsContext = createContext<any>({
  width: 0,
  ...breakpoints,
});

export const BreakpointsProvider = ({ children }: Props) => {
  const { ssr, deviceType } = useServer();

  const getInnerWidth = () => {
    if (!ssr) return window.innerWidth;

    if (deviceType === "mobile") {
      return breakpoints.phone.max;
    } else if (deviceType === "tablet") {
      return breakpoints.tablet.max;
    }

    return breakpoints.fullHD.max;
  };

  const [width, setWidth] = useState(getInnerWidth());

  const handleResize = () => {
    setWidth(getInnerWidth());
  };

  useEffect(() => {
    if (!window) return;
    window.addEventListener("resize", handleResize, { capture: true });

    return () => window.removeEventListener("resize", handleResize, { capture: true });
  }, []);

  const breakpointLessThan = (breakpoint: Breakpoint) => width < breakpoints[breakpoint].min;
  const breakpointGreaterThan = (breakpoint: Breakpoint) => width > breakpoints[breakpoint].max;
  const breakpointActive = (breakpoint: Breakpoint) => {
    return !breakpointLessThan(breakpoint) && !breakpointGreaterThan(breakpoint);
  };

  const breakpointDetails = (breakpoint: Breakpoint): BreakpointDetails => ({
    min: breakpoints[breakpoint].min,
    max: breakpoints[breakpoint].max,

    active: breakpointActive(breakpoint),
    greaterThan: breakpointGreaterThan(breakpoint),
    lessThan: breakpointLessThan(breakpoint),
  });

  const breakpointsActiveMap = Object.keys(breakpoints).reduce((map, breakpoint: Breakpoint) => {
    map[breakpoint] = breakpointDetails(breakpoint);

    return map;
  }, {});

  return (
    <BreakpointsContext.Provider
      value={{ width, ...(breakpointsActiveMap as BreakpointsActiveMap) }}
    >
      {children}
    </BreakpointsContext.Provider>
  );
};

export { BreakpointsContext, type BreakpointsActiveMap };
